import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  updateVarietyPriceEquivalence,
  createCategorySubcategory,
  updateCategorySubcategory,
  deleteCategorySubcategory,
  getBranches,
  getCashRegisters,
  getProducts,
} from "../api";
import CategoryFormModal from "../components/CategoryFormModal";
import CategoryInventoryReadinessModal from "../components/CategoryInventoryReadinessModal";
import useCurrentUser from "../hooks/useCurrentUser";
import { hasPermission } from "../utils/permissions";
import { normalizePaginatedResponse } from "../utils/pagination";
import InlineLoading from "../components/InlineLoading";
import useConfirmDialog from '../hooks/useConfirmDialog';
import useProductEvents from '../hooks/useProductEvents';

export const getProductCategorySnapshot = (product) => ({
  categoryId: product?.categoryId ?? product?.Category?.id ?? null,
  categoryName: product?.Category?.name || product?.categoryName || product?.categoryLabel || null,
  subcategoryId: product?.subcategoryId ?? product?.Subcategory?.id ?? null,
});

export const buildProductCategoryState = (products) => {
  const byId = {};
  const byName = {};
  const bySubcategoryId = {};
  const index = new Map();

  products.forEach((product) => {
    const snapshot = getProductCategorySnapshot(product);
    if (snapshot.categoryId) {
      byId[snapshot.categoryId] = (byId[snapshot.categoryId] || 0) + 1;
    }
    if (snapshot.categoryName) {
      byName[snapshot.categoryName] = (byName[snapshot.categoryName] || 0) + 1;
    }
    if (snapshot.subcategoryId) {
      bySubcategoryId[snapshot.subcategoryId] = (bySubcategoryId[snapshot.subcategoryId] || 0) + 1;
    }
    if (product?.id) index.set(String(product.id), snapshot);
  });

  return { counts: { byId, byName, bySubcategoryId }, index };
};

export const applyProductCountDelta = (counts, snapshot, delta) => {
  const next = {
    byId: { ...counts.byId },
    byName: { ...counts.byName },
    bySubcategoryId: { ...counts.bySubcategoryId },
  };
  const adjust = (map, key) => {
    map[key] = Math.max((map[key] || 0) + delta, 0);
  };
  if (snapshot.categoryId != null) adjust(next.byId, snapshot.categoryId);
  if (snapshot.categoryName) adjust(next.byName, snapshot.categoryName);
  if (snapshot.subcategoryId != null) adjust(next.bySubcategoryId, snapshot.subcategoryId);
  return next;
};

export const getSortedSubcategories = (category) => (
  (category?.CategorySubcategories || [])
    .slice()
    .sort((a, b) =>
      Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)
      || String(a?.name || '').localeCompare(String(b?.name || ''), 'es', { sensitivity: 'base' })
    )
);

const CategoriesAdminPage = ({
  branchId: branchIdProp,
  embedded = false,
  canUpdatePrices: canUpdatePricesProp,
}) => {
  const params = useParams();
  const branchId = branchIdProp ?? params.branchId ?? localStorage.getItem("selectedBranchId");
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [cashRegistersByBranch, setCashRegistersByBranch] = useState({});
  const [productCounts, setProductCounts] = useState({ byId: {}, byName: {}, bySubcategoryId: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [inventoryReadinessFailure, setInventoryReadinessFailure] = useState(null);
  const [pendingCategoryPayload, setPendingCategoryPayload] = useState(null);
  const [subcategoryDrafts, setSubcategoryDrafts] = useState({});
  const [subcategoryEdits, setSubcategoryEdits] = useState({});
  const { confirm } = useConfirmDialog();
  const { user } = useCurrentUser();
  const productCategoryIndexRef = useRef(new Map());

  const canCreateCategory = hasPermission(user, "category.create") || hasPermission(user, "category.manage");
  const canUpdateCategory = hasPermission(user, "category.update") || hasPermission(user, "category.manage");
  const canDeleteCategory = hasPermission(user, "category.delete") || hasPermission(user, "category.manage");
  const canUpdateProduct = hasPermission(user, "product.update");
  const canUpdatePrices = typeof canUpdatePricesProp === 'boolean'
    ? canUpdatePricesProp
    : hasPermission(user, "price.update");

  const getApiErrorMessage = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;

  const getInventoryReadinessFailure = (err) => {
    const data = err?.response?.data;
    if (data?.details?.type !== "category_inventory_readiness_failed") return null;
    return {
      message: data.message,
      details: data.details,
    };
  };

  const availabilityForCategory = (category) => {
    return (
      category?.CategoryAvailabilities
      || category?.CategoryAvailability
      || []
    );
  };

  const getProductCount = (category) => {
    const countById = productCounts.byId?.[category.id] || 0;
    if (countById > 0) return countById;
    if (!category.name) return countById;
    return productCounts.byName?.[category.name] || 0;
  };

  const getSubcategoryCount = (subcategory) => {
    if (!subcategory?.id) return 0;
    return productCounts.bySubcategoryId?.[subcategory.id] || 0;
  };

  const availabilitySummary = (category) => {
    const rows = availabilityForCategory(category);
    if (!rows.length) return "Sin disponibilidad";
    const branchMap = new Map();
    rows.forEach((row) => {
      const branchId = row?.branchId;
      if (!branchId) return;
      const key = String(branchId);
      const entry = branchMap.get(key) || { all: false, ids: new Set() };
      if (!row.cashRegisterId) {
        entry.all = true;
        entry.ids.clear();
      } else if (!entry.all) {
        entry.ids.add(String(row.cashRegisterId));
      }
      branchMap.set(key, entry);
    });

    const getRegistersForBranch = (branchKey) => {
      return cashRegistersByBranch[branchKey] || [];
    };

    let registerCount = 0;
    branchMap.forEach((entry, branchKey) => {
      if (entry.all) {
        const registers = getRegistersForBranch(branchKey);
        registerCount += registers.length;
        return;
      }
      registerCount += entry.ids.size;
    });

    return `${branchMap.size} sucursales, ${registerCount} cajas`;
  };

  const fetchData = async () => {
    if (!branchId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
        const [categoryData, branchData, productData] = await Promise.all([
          getCategories({ includeAvailability: true }),
          getBranches({ pageSize: 0 }),
          getProducts(branchId, { pageSize: 0 }),
        ]);
        setCategories(categoryData || []);
        setBranches(normalizePaginatedResponse(branchData).data);

      const normalizedProducts = normalizePaginatedResponse(productData);
      const nextState = buildProductCategoryState(normalizedProducts.data);
      setProductCounts(nextState.counts);
      productCategoryIndexRef.current = nextState.index;
    } catch (err) {
      setError(getApiErrorMessage(err, "Error cargando categorias."));
    } finally {
      setLoading(false);
    }
  };

  const refreshProductCounts = useCallback(async () => {
    if (!branchId) return;
    try {
      const productData = await getProducts(branchId, { pageSize: 0 });
      const normalizedProducts = normalizePaginatedResponse(productData);
      const nextState = buildProductCategoryState(normalizedProducts.data);
      setProductCounts(nextState.counts);
      productCategoryIndexRef.current = nextState.index;
    } catch (err) {
      console.error("Error refreshing product counts:", err);
    }
  }, [branchId]);

  useEffect(() => {
    fetchData();
  }, [branchId]);

  const handleProductEvent = useCallback((event) => {
    const action = event?.action;
    if (!action) return;
    if (!["create-product", "update-product", "delete-product"].includes(action)) return;
    const productId = event?.productId ?? event?.product?.id ?? event?.productPatch?.id;
    if (!productId) return;
    const key = String(productId);
    const previous = productCategoryIndexRef.current.get(key) || {
      categoryId: null,
      categoryName: null,
      subcategoryId: null,
    };
    if (action === "delete-product") {
      if (!previous.categoryId && !previous.categoryName && !previous.subcategoryId) return;
      setProductCounts((prev) => applyProductCountDelta(prev, previous, -1));
      productCategoryIndexRef.current.delete(key);
      return;
    }

    const product = event?.product || event?.productPatch;
    if (!product) {
      refreshProductCounts();
      return;
    }
    const {
      categoryId: nextCategoryId,
      categoryName: nextCategoryName,
      subcategoryId: nextSubcategoryId,
    } = getProductCategorySnapshot(product);

    if (action === "create-product") {
      setProductCounts((prev) => applyProductCountDelta(prev, {
        categoryId: nextCategoryId,
        categoryName: nextCategoryName,
        subcategoryId: nextSubcategoryId,
      }, 1));
      productCategoryIndexRef.current.set(key, {
        categoryId: nextCategoryId,
        categoryName: nextCategoryName,
        subcategoryId: nextSubcategoryId,
      });
      return;
    }

    const changedId = previous.categoryId !== nextCategoryId;
    const changedName = previous.categoryName !== nextCategoryName;
    const changedSubcategory = previous.subcategoryId !== nextSubcategoryId;
    if (!changedId && !changedName && !changedSubcategory) {
      productCategoryIndexRef.current.set(key, {
        categoryId: nextCategoryId,
        categoryName: nextCategoryName,
        subcategoryId: nextSubcategoryId,
      });
      return;
    }

    setProductCounts((prev) => applyProductCountDelta(
      applyProductCountDelta(prev, previous, -1),
      { categoryId: nextCategoryId, categoryName: nextCategoryName, subcategoryId: nextSubcategoryId },
      1
    ));
    productCategoryIndexRef.current.set(key, {
      categoryId: nextCategoryId,
      categoryName: nextCategoryName,
      subcategoryId: nextSubcategoryId,
    });
  }, [refreshProductCounts]);

  useProductEvents(branchId, handleProductEvent);

  useEffect(() => {
    const fetchRegisters = async () => {
      if (!branches.length) return;
      const map = {};
      await Promise.all(
        branches.map(async (branch) => {
          try {
            const data = await getCashRegisters(branch.id, { pageSize: 0 });
            map[branch.id] = normalizePaginatedResponse(data).data;
          } catch (err) {
            map[branch.id] = [];
          }
        })
      );
      setCashRegistersByBranch(map);
    };
    fetchRegisters();
  }, [branches]);

  const closeForm = () => {
    setFormOpen(false);
    setEditingCategory(null);
    setFormError("");
    setInventoryReadinessFailure(null);
    setPendingCategoryPayload(null);
  };

  const handleSave = async (payload) => {
    const categoryPayload = { ...(payload || {}) };
    if (!canUpdatePrices) delete categoryPayload.pricingBasis;
    const currentPricingBasis = editingCategory?.pricingBasis === 'kg' ? 'kg' : 'unit';
    const nextPricingBasis = categoryPayload.pricingBasis === 'kg' ? 'kg' : 'unit';
    const updatesPricingBasis = canUpdatePrices
      && Object.hasOwn(categoryPayload, 'pricingBasis');
    if (editingCategory && updatesPricingBasis && currentPricingBasis !== nextPricingBasis) {
      const approved = await confirm({
        title: 'Cambiar base de precios',
        message: 'Se convertirán costos, reglas y márgenes de los productos que heredan esta categoría. Los formatos sin peso quedarán pendientes sin cambiar su precio de venta.',
        confirmLabel: 'Cambiar y convertir',
        cancelLabel: 'Cancelar',
        tone: 'primary',
      });
      if (!approved) return;
    }
    setSaving(true);
    setError("");
    setFormError("");
    setInventoryReadinessFailure(null);
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, categoryPayload);
      } else {
        await createCategory(categoryPayload);
      }
      await fetchData();
      closeForm();
    } catch (err) {
      const readinessFailure = getInventoryReadinessFailure(err);
      if (readinessFailure) {
        setPendingCategoryPayload(categoryPayload);
        setInventoryReadinessFailure(readinessFailure);
      } else {
        setFormError(getApiErrorMessage(err, "Error guardando categoria."));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveInventoryReadinessFixes = async (fixes) => {
    if (!pendingCategoryPayload) {
      throw new Error("No hay cambios de categoría pendientes.");
    }
    setSaving(true);
    setError("");
    setFormError("");
    try {
      await Promise.all(
        fixes.map((fix) => updateVarietyPriceEquivalence(branchId, fix.varietyPriceId, {
          baseQuantityPerUnit: fix.baseQuantityPerUnit,
        }))
      );
      if (editingCategory) {
        await updateCategory(editingCategory.id, pendingCategoryPayload);
      } else {
        await createCategory(pendingCategoryPayload);
      }
      await fetchData();
      closeForm();
    } catch (err) {
      const readinessFailure = getInventoryReadinessFailure(err);
      if (readinessFailure) {
        setInventoryReadinessFailure(readinessFailure);
        throw new Error(readinessFailure.message || "Aún quedan formatos incompletos.");
      }
      throw new Error(getApiErrorMessage(err, "No se pudieron guardar las equivalencias."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category) => {
    const approved = await confirm({
      title: 'Eliminar categoría',
      message: `Eliminar categoria "${category.name}"?`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    setSaving(true);
    setError("");
    try {
      await deleteCategory(category.id);
      await fetchData();
    } catch (err) {
      setError(getApiErrorMessage(err, "Error eliminando categoria."));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSubcategory = async (category) => {
    if (!category?.id) return;
    const name = String(subcategoryDrafts[category.id] || '').trim();
    if (!name) return;
    setSaving(true);
    setError("");
    try {
      await createCategorySubcategory(category.id, { name });
      setSubcategoryDrafts((prev) => ({ ...prev, [category.id]: "" }));
      await fetchData();
    } catch (err) {
      setError(getApiErrorMessage(err, "Error creando subcategoría."));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSubcategory = async (category, subcategory) => {
    if (!category?.id || !subcategory?.id) return;
    const name = String(subcategoryEdits[subcategory.id]).trim();
    if (!name) return;
    setSaving(true);
    setError("");
    try {
      await updateCategorySubcategory(category.id, subcategory.id, { name });
      setSubcategoryEdits((prev) => {
        const next = { ...prev };
        delete next[subcategory.id];
        return next;
      });
      await fetchData();
    } catch (err) {
      setError(getApiErrorMessage(err, "Error actualizando subcategoría."));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubcategory = async (category, subcategory) => {
    if (!category?.id || !subcategory?.id) return;
    const approved = await confirm({
      title: 'Eliminar subcategoría',
      message: `Eliminar subcategoría "${subcategory.name}"?`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    setSaving(true);
    setError("");
    try {
      await deleteCategorySubcategory(category.id, subcategory.id);
      await fetchData();
    } catch (err) {
      setError(getApiErrorMessage(err, "Error eliminando subcategoría."));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={embedded ? "" : "p-6"}>
        <InlineLoading label="Cargando categorias" />
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "p-6 max-w-5xl mx-auto"}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Categorias</h1>
          <p className="text-sm text-gray-600">
            Crea categorias, define su base de precios y asignalas a sucursales y cajas.
          </p>
        </div>
        {canCreateCategory && (
          <button
            onClick={() => {
              setEditingCategory(null);
              setFormError("");
              setInventoryReadinessFailure(null);
              setPendingCategoryPayload(null);
              setFormOpen(true);
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Nueva categoria
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="grid gap-4">
        {categories.map((category) => {
          const productCount = getProductCount(category);
          const canDelete = productCount === 0;
          return (
            <div key={category.id} className="bg-white rounded shadow p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-semibold">{category.name}</h2>
                    {category.isResaleOnly && (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                        Reventa
                      </span>
                    )}
                    {category.tracksInventory && (
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                        Inventario
                      </span>
                    )}
                    <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700">
                      {category.pricingBasis === 'kg' ? 'Precios por kg' : 'Precios por unidad'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Estado: {category.isActive ? "Activa" : "Inactiva"} · Productos: {productCount}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {availabilitySummary(category)}
                  </p>
                </div>
                <div className="flex gap-2">
                  {canUpdateCategory && (
                    <button
                      className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
                      onClick={() => {
                        setEditingCategory(category);
                        setFormError("");
                        setInventoryReadinessFailure(null);
                        setPendingCategoryPayload(null);
                        setFormOpen(true);
                      }}
                    >
                      Editar
                    </button>
                  )}
                  {canDeleteCategory && (
                    <button
                      className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 text-sm disabled:opacity-50"
                      onClick={() => handleDelete(category)}
                      disabled={!canDelete || saving}
                      title={canDelete ? "" : "La categoria tiene productos asociados"}
                    >
                      Eliminar
                    </button>
                  )}
                </div>
              </div>
              {category.description && (
                <p className="text-sm text-gray-700 mt-2">
                  {category.description}
                </p>
              )}
              <div className="mt-4 border-t border-gray-100 pt-3">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">Subcategorías</h3>
                    <p className="text-xs text-gray-500">
                      Opcionales para agrupar productos dentro de esta categoría.
                    </p>
                  </div>
                  {canUpdateCategory && (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={subcategoryDrafts[category.id] || ""}
                        onChange={(event) =>
                          setSubcategoryDrafts((prev) => ({
                            ...prev,
                            [category.id]: event.target.value,
                          }))
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleCreateSubcategory(category);
                          }
                        }}
                        placeholder="Nueva subcategoría"
                        className="border border-gray-300 rounded px-2 py-1 text-sm"
                        disabled={saving}
                      />
                      <button
                        type="button"
                        className="px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 text-sm disabled:opacity-50"
                        onClick={() => handleCreateSubcategory(category)}
                        disabled={saving || !String(subcategoryDrafts[category.id] || "").trim()}
                      >
                        Agregar
                      </button>
                    </div>
                  )}
                </div>

                {getSortedSubcategories(category).length > 0 ? (
                  <div className="space-y-2">
                    {getSortedSubcategories(category).map((subcategory) => {
                      const productCount = getSubcategoryCount(subcategory);
                      const editValue = subcategoryEdits[subcategory.id];
                      const isEditing = editValue !== undefined;
                      return (
                        <div
                          key={subcategory.id}
                          className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                        >
                          <div className="min-w-0">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editValue}
                                onChange={(event) =>
                                  setSubcategoryEdits((prev) => ({
                                    ...prev,
                                    [subcategory.id]: event.target.value,
                                  }))
                                }
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") {
                                    event.preventDefault();
                                    handleUpdateSubcategory(category, subcategory);
                                  }
                                  if (event.key === "Escape") {
                                    setSubcategoryEdits((prev) => {
                                      const next = { ...prev };
                                      delete next[subcategory.id];
                                      return next;
                                    });
                                  }
                                }}
                                className="border border-gray-300 rounded px-2 py-1 text-sm"
                                autoFocus
                              />
                            ) : (
                              <div className="text-sm font-medium text-gray-800 truncate">
                                {subcategory.name}
                              </div>
                            )}
                            <div className="text-xs text-gray-500">
                              Productos: {productCount}
                            </div>
                          </div>
                          {canUpdateCategory && (
                            <div className="flex gap-2">
                              {isEditing ? (
                                <>
                                  <button
                                    type="button"
                                    className="px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 text-sm disabled:opacity-50"
                                    onClick={() => handleUpdateSubcategory(category, subcategory)}
                                    disabled={saving || !String(editValue || "").trim()}
                                  >
                                    Guardar
                                  </button>
                                  <button
                                    type="button"
                                    className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
                                    onClick={() =>
                                      setSubcategoryEdits((prev) => {
                                        const next = { ...prev };
                                        delete next[subcategory.id];
                                        return next;
                                      })
                                    }
                                    disabled={saving}
                                  >
                                    Cancelar
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-sm"
                                    onClick={() =>
                                      setSubcategoryEdits((prev) => ({
                                        ...prev,
                                        [subcategory.id]: subcategory.name || "",
                                      }))
                                    }
                                    disabled={saving}
                                  >
                                    Editar
                                  </button>
                                  <button
                                    type="button"
                                    className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 text-sm disabled:opacity-50"
                                    onClick={() => handleDeleteSubcategory(category, subcategory)}
                                    disabled={saving || productCount > 0}
                                    title={productCount > 0 ? "La subcategoría tiene productos asociados" : ""}
                                  >
                                    Eliminar
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    Sin subcategorías.
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {!categories.length && (
          <div className="bg-white rounded shadow p-6 text-sm text-gray-600">
            No hay categorias creadas.
          </div>
        )}
      </div>

      <CategoryFormModal
        isOpen={formOpen}
        onClose={closeForm}
        onSave={handleSave}
        branches={branches}
        cashRegistersByBranch={cashRegistersByBranch}
        initialData={editingCategory}
        isSaving={saving}
        submitError={formError}
        canUpdatePrices={canUpdatePrices}
      />

      <CategoryInventoryReadinessModal
        isOpen={Boolean(inventoryReadinessFailure)}
        failure={inventoryReadinessFailure}
        branchId={branchId}
        canUpdateProduct={canUpdateProduct}
        isSaving={saving}
        onClose={() => setInventoryReadinessFailure(null)}
        onSaveAll={handleSaveInventoryReadinessFixes}
      />
    </div>
  );
};

export default CategoriesAdminPage;
